import { FlowType } from "@redotech/redo-model/return-flow";
import { Team } from "@redotech/redo-model/team";
import { Step, StepId } from "@redotech/redo-return-ui/flows/step";
import { Edge, MarkerType, Node } from "@xyflow/react";
import { MutableRefObject, useMemo } from "react";
import { NodeData } from "../components/flow-step-node";
import { createEdgesFromSteps } from "../utils/edges-from-steps";
import { createNodesFromSteps } from "../utils/nodes-from-steps";

export const PATH_HIGHLIGHT_COLOR = "#FF4405";

interface HighlightParams {
  highlightedNodeIds: Set<string>;
  currentStepId?: string;
  highlightedEdgePairs?: Set<string>;
}

interface UseFlowNodesAndEdgesParams {
  steps: Map<StepId, Step>;
  flowType: FlowType;
  nodePositions: Map<string, { x: number; y: number }>;
  selectedNodeIdsRef: MutableRefObject<Set<string>>;
  team?: Team;
  highlight?: HighlightParams;
}

interface UseFlowNodesAndEdgesResult {
  nodes: Node<NodeData>[];
  edges: Edge[];
  computedPositions: Map<string, { x: number; y: number }>;
}

export function useFlowNodesAndEdges({
  steps,
  flowType,
  nodePositions,
  selectedNodeIdsRef,
  team,
  highlight,
}: UseFlowNodesAndEdgesParams): UseFlowNodesAndEdgesResult {
  return useMemo(() => {
    if (steps.size === 0) {
      return {
        nodes: [] as Node<NodeData>[],
        edges: [] as Edge[],
        computedPositions: new Map(),
      };
    }

    const { nodes: positionedNodes, nodePositions: computedPositions } =
      createNodesFromSteps(
        steps,
        flowType,
        nodePositions,
        selectedNodeIdsRef,
        team,
      );

    const validNodeIds = new Set(positionedNodes.map((node) => node.id));

    const rawEdges = createEdgesFromSteps(
      steps,
      computedPositions,
      selectedNodeIdsRef,
      validNodeIds,
    );

    if (!highlight) {
      return { nodes: positionedNodes, edges: rawEdges, computedPositions };
    }

    const nodes = positionedNodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        pathHighlighted: highlight.highlightedNodeIds.has(node.id),
        isCurrentStep: node.id === highlight.currentStepId,
      },
    }));

    const edges = highlight.highlightedEdgePairs
      ? rawEdges.map((edge) => {
          const isHighlighted = highlight.highlightedEdgePairs!.has(
            `${edge.source}-${edge.target}`,
          );
          if (!isHighlighted) {
            return edge;
          }
          return {
            ...edge,
            style: { stroke: PATH_HIGHLIGHT_COLOR, strokeWidth: 2.5 },
            markerEnd: { type: MarkerType.Arrow, color: PATH_HIGHLIGHT_COLOR },
          };
        })
      : rawEdges;

    return { nodes, edges, computedPositions };
  }, [steps, flowType, nodePositions, selectedNodeIdsRef, team, highlight]);
}
